import { useEffect } from 'react';

import { useIntl } from 'react-intl';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import {
	AnchorPageRelayQuery,
	shapeAnchorContentfulResponse,
} from 'src/contentful/graphql/anchor-page';

import { anchorPageRelayQuery } from '@/__generated_contentful__/anchorPageRelayQuery.graphql';
import { CarouselProps } from '@/components/confluence/carousel/src/carousel';
import { useLogMeInD0to6 } from '@/components/confluence/hooks/use-log-me-in-d0to6.tsx';
import { PaidHeroCarousel } from '@/patterns/paid-hero/src/paid-hero-carousel';
import HeroSection from '@/templates/software/confluence/confluence-software-page/src/sections/log-me-in/hero';
import { BanditsProvider } from '@/utils/anchor-queries/bandits-components.tsx';
import {
	AnchorContentsResponse,
	PaidLandingPersonaTypes,
} from '@/utils/anchor-queries/get-anchor-contents';
import { BANDITS_PRODUCT, BANDITS_USE_CASE } from '@/utils/anchor-queries/get-bandits-contents';
import { experiments } from '@/utils/experiment/manifest';
import { useExperiment } from '@/utils/experiment/use-experiment';
import { useExperimentExposure } from '@/utils/experiment/use-experiment-exposure';
import { FeatureExposed } from '@/utils/feature-exposed/use-feature-exposed';

import { ConfluenceSoftwarePageProps } from './confluence-software-page';
import { ConfluencePagePaidLpConflAnchor } from './paid-lp-confl-anchor.tsx';
import { isTargetedSubAnchor } from './subAnchors/subAnchor-util.tsx';

export interface ConfluencePageAnchorLpConfWorkManagementRelayProps extends ConfluenceSoftwarePageProps {
	anchorContents?: AnchorContentsResponse | null;
	persona?: string;
	queryRef?: PreloadedQuery<anchorPageRelayQuery>;
}
export const ConfluenceSoftwarePageAnchorWorkManagement = (
	pageProps: ConfluencePageAnchorLpConfWorkManagementRelayProps,
) => {
	const intl = useIntl();
	const [exposureSubAnchorFired, fireSubAnchorExposure] = useExperimentExposure(
		experiments.cfind4782LandingPageSubAnchorV3,
	);
	useEffect(() => {
		if (isTargetedSubAnchor(pageProps.subAnchor) && !exposureSubAnchorFired) {
			fireSubAnchorExposure();
		}
	}, [exposureSubAnchorFired, fireSubAnchorExposure]);

	const gwpAJContextualBanditsConfluenceSubAnchor = useExperiment(
		experiments.gwpAJContextualBanditsConfluenceSubAnchor,
	);
	const isCBSubAnchorExperimentEnabled =
		gwpAJContextualBanditsConfluenceSubAnchor.type === 'enrolled' &&
		gwpAJContextualBanditsConfluenceSubAnchor.value;

	const data = usePreloadedQuery(
		AnchorPageRelayQuery,
		pageProps.queryRef || (null as unknown as PreloadedQuery<anchorPageRelayQuery>),
	);
	pageProps = Object.assign({}, pageProps, {
		anchorContents: shapeAnchorContentfulResponse(data),
	});

	const { userProfileName, recentSites, shouldShowLogMeIn } = useLogMeInD0to6({
		availableProducts: pageProps.availableProducts,
		userProfile: pageProps.userProfile,
	});

	const carouselSlides = [
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.WorkManagement.move-work-forward.title',
				defaultMessage: 'Move work forward',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.WorkManagement.move-work-forward.body',
				defaultMessage: 'Plan projects, take notes, and brainstorm — all in one place.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/1e81unagi717g13fj44qtgpk0h8r0537.webp',
			imgAltText: 'A Confluence page being edited',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.WorkManagement.work-better-together.title',
				defaultMessage: 'Work better, together',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.WorkManagement.work-better-together.body',
				defaultMessage: 'Collaborate with your teams with real-time editing and inline comments.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/i0011ky0775062fk18784clr4dcxd5wg.webp',
			imgAltText: 'A Confluence page with multiple teammates editing and leaving comments',
		},
		{
			primaryText: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.WorkManagement.connect-all-your-work.title',
				defaultMessage: 'Connect all your work',
			}),
			description: intl.formatMessage({
				id: 'ConfluenceSoftwarePageTemplate.WorkManagement.connect-all-your-work.body',
				defaultMessage: 'Stay organized and up-to-date with dedicated team workspaces.',
			}),
			imgSrc: 'https://dam-cdn.atl.orangelogic.com/AssetLink/fpd20037c363n21cfmh45nc5m50co161.webp',
			imgAltText: 'Organized workspace in Confluence',
		},
	];

	const carouselHeroProps: CarouselProps = {
		'aria-label': 'A display of various confluence features',
		id: 'confluence-work-management-carousel',
		slides: carouselSlides,
	};

	const pageId = 'anchor-lp-conf-work-management';

	const isLoggedIn = !!userProfileName;
	let customHeroSection;

	const getLiftShiftHeroSectionBrandUpdate = () => {
		return (
			<PaidHeroCarousel
				pageId={pageId}
				embeddedSignup={{ bundle: 'confluence' }}
				carouselSlides={carouselSlides}
				shouldUseNewBrandBackground
				shouldUseTransparentBackground
			/>
		);
	};

	if (isLoggedIn) {
		if (shouldShowLogMeIn) {
			customHeroSection = (
				<FeatureExposed experimentKey="cfind-2801-get-paid-users-back-to-product" group="Test">
					<HeroSection
						confluenceSites={recentSites}
						userProfileName={userProfileName}
						hideExploreMore
						loggedInPaid
					/>
				</FeatureExposed>
			);
		} else {
			customHeroSection = getLiftShiftHeroSectionBrandUpdate();
		}
	} else {
		// Logged out user
		customHeroSection = null;
	}

	return (
		<BanditsProvider
			product={BANDITS_PRODUCT.CONFLUENCE}
			keyword={pageProps.keyword}
			isExperimentEnabled={isCBSubAnchorExperimentEnabled}
			useCase={BANDITS_USE_CASE.ANCHOR_JOURNEY_SUB_ANCHOR}
			subAnchor={pageProps.subAnchor || ''}
			channel={pageProps.utmMedium || 'paid-search'}
			utmContent={pageProps.utmContent || ''}
			utmCampaign={pageProps.utmCampaign || ''}
		>
			<FeatureExposed experimentKey="cfind-3090-paid-confluence-product-tour" group="Test">
				<ConfluencePagePaidLpConflAnchor
					{...pageProps}
					pageId={pageId}
					persona={'unknown' as PaidLandingPersonaTypes}
					pageVariant="brandUpdate"
					dataProductKey="confluence-PaidLpConfWorkManagementAutomation"
					keyword={pageProps.keyword}
					carouselHeroProps={carouselHeroProps}
					customHeroSection={customHeroSection}
				/>
			</FeatureExposed>
		</BanditsProvider>
	);
};
